import React from 'react';

import {
	ordinalSuffix,
	formatGameTeamDetails,
	getPlayerLinkWithNumberAndPosition,
	formatNumberWithDecimal,
} from './shared_helpers';
import { preserveQueryParams } from './browser_helpers';

export const cflTeamNameIDArrayMap = new Map([
	[5611, 'hamilton-tiger-cats'],
	[5612, 'montreal-alouettes'],
	[27755, 'ottawa-redblacks'],
	[5615, 'toronto-argonauts'],
	[5608, 'bc-lions'],
	[5609, 'calgary-stampeders'],
	[5610, 'edmonton-elks'],
	[5614, 'saskatchewan-roughriders'],
	[5616, 'winnipeg-blue-bombers'],
]);
import { sortNumberText } from './shared_helpers';

export function formatSeasonLeaders(homeTeam, visitingTeam) {
	const leaderCategories = [
		{
			title: 'Passing Yards',
			categoryKey: 'passing_leader',
			valueKey: 'yards',
		},
		{
			title: 'Rushing Yards',
			categoryKey: 'rushing_leader',
			valueKey: 'rushing_yards', // check if there in a rushing_yards value to handle cases where the QB is a passing leader as well as a rushing leader.
			alternateValueKey: 'yards',
		},
		{
			title: 'Receiving Yards',
			categoryKey: 'receiving_leader',
			valueKey: 'yards',
		},
		{
			title: 'Tackles',
			categoryKey: 'tackles_leader',
			valueKey: 'tackles',
		},
		{
			title: 'Sacks',
			categoryKey: 'sacks_leader',
			valueKey: 'sacks',
			decimalPlaces: 1,
		},
		{
			title: 'Interceptions',
			categoryKey: 'interceptions_leader',
			valueKey: 'interceptions',
		},
	];

	const seasonLeaders = leaderCategories.map(leaderCategory => {
		const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey];
		const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey];

		if (!homeTeamData || !visitingTeamData) {
			return null;
		}

		let homeTeamValue = homeTeamData[leaderCategory.valueKey];
		let visitingTeamValue = visitingTeamData[leaderCategory.valueKey];

		if (leaderCategory.alternateValueKey) {
			homeTeamValue = homeTeamValue || homeTeamData[leaderCategory.alternateValueKey];
			visitingTeamValue = visitingTeamValue || visitingTeamData[leaderCategory.alternateValueKey];
		}

		if (leaderCategory.decimalPlaces) {
			homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces);
			visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces);
		}

		return {
			title: leaderCategory.title,
			homeTeamShortName: homeTeam.short_name,
			homeTeamColor: homeTeam.color,
			visitingTeamColor: visitingTeam.color,
			visitingTeamShortName: visitingTeam.short_name,
			homeTeamPlayerImage: homeTeamData.image_urls.xs,
			visitingTeamPlayerImage: visitingTeamData.image_urls.xs,
			homeTeamLogo: homeTeam.image_url_90,
			visitingTeamLogo: visitingTeam.image_url_90,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
			visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${visitingTeamData.last_name}`,
			homeTeamDetails: {
				first_name: homeTeamData.first_name,
				last_name: homeTeamData.last_name,
				id: homeTeamData.id,
			},
			visitingTeamDetails: {
				first_name: visitingTeamData.first_name,
				last_name: visitingTeamData.last_name,
				id: visitingTeamData.id,
			},
		};
	});
	return seasonLeaders.filter(n => n); // filter out null values
}

export function formatBoxscore(team) {
	// sections come back null for games without player stats
	const section = key => team?.[key] || [];
	var passing = section('passing'),
		rushing = section('rushing'),
		receiving = section('receiving'),
		kickings = section('kickings'),
		punts = section('punts'),
		kick_returns = section('kick_returns'),
		punt_returns = section('punt_returns'),
		defense = section('defense');
	defense = defense.filter(obj => {
		return obj.tackles || obj.sacks || obj.interceptions || obj.yards || obj.passes_defensed;
	});
	return [
		{
			title: 'Passing',
			columns: [
				{
					title: 'COMP/ATT',
					dataKey: 'passesCompletedAttempted',
					tooltipText: 'Passes Completed/Passes Attempted',
				},
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{ title: 'TD', dataKey: 'touchdowns', tooltipText: 'Touchdowns' },
				{ title: 'INT', dataKey: 'interceptions', tooltipText: 'Interceptions' },
				{},
			],
			data: passing.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					passesCompletedAttempted: `${player.passes_completed}/${player.passes_attempted}`,
					yards: player.yards,
					touchdowns: player.touchdowns,
					interceptions: player.interceptions,
				};
			}),
		},
		{
			title: 'Rushing',
			columns: [
				{ title: 'ATT', dataKey: 'rushesAttempted', tooltipText: 'Rushes attempted' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{ title: 'TD', dataKey: 'touchdowns', tooltipText: 'Touchdowns' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestRush', tooltipText: 'Longest Rush' },
			],
			data: rushing.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					rushesAttempted: player.rushes_attempted,
					yards: player.yards,
					average: player.average,
					touchdowns: player.touchdowns,
					longestRush: player.longest_rush,
				};
			}),
		},
		{
			title: 'Receiving',
			columns: [
				{ title: 'REC', dataKey: 'receptions', tooltipText: 'Receptions' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{ title: 'TD', dataKey: 'touchdowns', tooltipText: 'Touchdowns' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestReceiving', tooltipText: 'Longest receiving' },
			],
			data: receiving.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					receptions: player.receptions,
					yards: player.yards,
					average: player.average,
					touchdowns: player.touchdowns,
					longestReceiving: player.longest_receiving,
				};
			}),
		},
		{
			title: 'Kicking',
			columns: [
				{ title: 'FG', dataKey: 'made', tooltipText: 'Made' },
				{ title: 'LNG', dataKey: 'longestkick', tooltipText: 'Longest Kick' },
				{ title: 'XP', dataKey: 'extraPoints', tooltipText: 'Targets' },
				{},
				{},
			],
			data: kickings.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					made: player.made,
					longestkick: player.longest_kick,
					extraPoints: formatExtraPoints(player),
				};
			}),
		},
		{
			title: 'Punting',
			columns: [
				{ title: 'P', dataKey: 'punts', tooltipText: 'Punts' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestPunt', tooltipText: 'Longest Punt' },
				{},
			],
			data: punts.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					punts: player.punts,
					yards: player.yards,
					average: player.average,
					longestPunt: player.longest_punt,
				};
			}),
		},
		{
			title: 'Kick Returns',
			columns: [
				{ title: 'KR', dataKey: 'returns', tooltipText: 'Returns' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{
					title: 'LNG',
					dataKey: 'longestKickReturn',
					tooltipText: 'Longest Kick Return',
				},
				{},
			],
			data: kick_returns.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					returns: player.returns,
					yards: player.yards,
					average: player.average,
					longestKickReturn: player.longest_kick_return,
				};
			}),
		},

		{
			title: 'Punt Returns',
			columns: [
				{ title: 'PR', dataKey: 'returns', tooltipText: 'Returns' },
				{ title: 'YDS', dataKey: 'yards', tooltipText: 'Yards' },
				{
					title: 'AVG',
					dataKey: 'average',
					tooltipText: 'Average',
					sortFunction: sortNumberText,
				},
				{ title: 'LNG', dataKey: 'longestPuntReturn', tooltipText: 'Longest Punt Return' },
				{},
			],
			data: punt_returns.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					returns: player.returns,
					yards: player.yards,
					average: player.average,
					longestPuntReturn: player.longest_punt_return,
				};
			}),
		},
		{
			title: 'Defense',
			columns: [
				{ title: 'TCKL', dataKey: 'tackles', tooltipText: 'Tackles' },
				{ title: 'SACK', dataKey: 'sacks', tooltipText: 'Sacks' },
				{
					title: 'INT',
					dataKey: 'interceptions',
					tooltipText: 'Interceptions',
				},
				{ title: 'PDEF', dataKey: 'passesDefended', tooltipText: 'Passes Defended' },
				{},
			],
			data: defense.map(player => {
				return {
					name: getPlayerLinkWithNumberAndPosition(player),
					tackles: player.tackles,
					sacks: player.sacks,
					yards: player.yards,
					interceptions: player.interceptions,
					passesDefended: player.passes_defensed,
				};
			}),
		},
	];
}
export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
	const ghostPlays = [
		{ id: 1, isGhost: true },
		{ id: 2, isGhost: true },
		{ id: 3, isGhost: true },
		{ id: 4, isGhost: true },
		{ id: 5, isGhost: true },
		{ id: 6, isGhost: true },
		{ id: 7, isGhost: true },
		{ id: 8, isGhost: true },
		{ id: 9, isGhost: true },
	];

	if (!lastNineEvents) return ghostPlays;

	const massagedPlays = lastNineEvents.map(play => {
		const formattedPlayDescription = formatTeamYardsInText(play.description);
		const teamLogo = play.possession === homeTeam.short_name ? homeTeam.image_url_90 : visitingTeam.image_url_90;

		return {
			id: play.id,
			title: setPlayTitle(play),
			color: getPlayColor(play.play_type_id),
			bodyText: shortenPlayerNames(formattedPlayDescription, play.players),
			teamLogo: teamLogo,
		};
	});

	return massagedPlays.concat(ghostPlays.slice(massagedPlays.length));
}

function formatTeamYardsInText(originalText = '') {
	const teamYardageTexts = originalText.match(/[a-zA-Z]+[0-9]+/gm) || [];
	if (!teamYardageTexts.length) {
		return originalText;
	}

	teamYardageTexts.forEach(teamYardage => {
		const teamTricode = (teamYardage.match(/[a-zA-Z]+/g)[0] || '').toUpperCase();
		const teamYards = teamYardage.match(/[0-9]+/g)[0] || '';
		originalText = originalText.replace(teamYardage, `${teamTricode} ${teamYards}`);
	});

	return originalText;
}

function setPlayTitle(play) {
	const yardlineTeamRegex = play.yardline.match(/[a-zA-Z]+/g) || [];
	const yardLineDistanceRegex = play.yardline.match(/[0-9]+/g);
	const yardLineTeam = (yardlineTeamRegex[0] || '').toUpperCase();
	const yardLineDistance = yardLineDistanceRegex[0] || '';

	const playTypeText = play.play_type || '';

	switch (play.play_type_id) {
		case 5:
			return 'KICKOFF';
		case 6:
			return 'KICKOFF RETURN';
		case 10:
		case 11:
			return 'PENALTY';
		case 41:
			return 'ONSIDE KICKOFF';
		case 47:
			return 'BLOCKED EXTRA POINT';
		case 52:
			return 'TWO POINT CONVERSION';
		case 53:
		case 54:
		case 56:
			return 'MISSED TWO POINT CONVERSION';
		case 57:
		case 58:
			return 'TIMEOUT';
		case 22:
		case 55:
			return playTypeText.toUpperCase();
		default:
			return `${ordinalSuffix(play.down).toUpperCase()} & ${play.distance} / ${yardLineTeam} ${yardLineDistance}`;
	}
}

function getPlayColor(playTypeId) {
	const darkBlue = 'darkBlue';
	const red = 'red';
	const grey = 'grey';
	const brightBlue = 'brightBlue';

	const playColorMap = new Map([
		[1, darkBlue],
		[2, darkBlue],
		[3, brightBlue],
		[4, darkBlue],
		[5, grey],
		[6, darkBlue],
		[7, grey],
		[8, darkBlue],
		[9, brightBlue],
		[10, red],
		[11, red],
		[12, ''],
		[13, ''],
		[14, brightBlue],
		[15, ''],
		[16, ''],
		[17, darkBlue],
		[18, darkBlue],
		[19, ''],
		[20, darkBlue],
		[21, ''],
		[22, darkBlue],
		[23, ''],
		[24, ''],
		[25, ''],
		[26, ''],
		[27, ''],
		[28, ''],
		[29, ''],
		[30, ''],
		[31, ''],
		[32, ''],
		[33, ''],
		[34, ''],
		[35, darkBlue],
		[36, ''],
		[37, ''],
		[38, ''],
		[39, ''],
		[40, ''],
		[41, grey],
		[42, darkBlue],
		[43, ''],
		[44, ''],
		[45, ''],
		[46, ''],
		[47, darkBlue],
		[48, ''],
		[49, ''],
		[50, ''],
		[51, ''],
		[52, darkBlue],
		[53, darkBlue],
		[54, darkBlue],
		[55, darkBlue],
		[56, darkBlue],
		[57, grey],
		[58, grey],
	]);
	return playColorMap.get(playTypeId) || grey;
}

function shortenPlayerNames(originalText, players = []) {
	let newText = originalText;

	if (!players.length) {
		return originalText;
	}

	for (const player of players) {
		const textToReplace = `${player.first_name} ${player.last_name}`;
		const playerInitial = player.first_name.charAt(0);
		const replacementText = `<b>${playerInitial}. ${player.last_name}</b>`;

		newText = newText.replace(textToReplace, replacementText);
	}
	return newText;
}

function formatExtraPoints(playerData) {
	if (playerData.extra_points_made && playerData.extra_points_attempted) {
		return `${playerData.extra_points_made}/${playerData.extra_points_attempted}`;
	} else {
		return null;
	}
}

export function formatPlayerName(player) {
	return `${player.first_name.charAt(0)}. ${player.last_name}`;
}

export function isGameTypePostseason(gameTypeID) {
	switch (gameTypeID) {
		case 13:
		case 14:
		case 15:
			return true;
		default:
			return false;
	}
}

export function formatGamecardData(game = {}, options = {}) {
	const gameStatus = game.game_status;
	const {
		visitingTeamName,
		visitingTeamLogo,
		homeTeamName,
		homeTeamLogo,
		broadcastName,
		broadcastString,
		gameLoser,
		gameTime,
		gameDate,
	} = formatGameTeamDetails(game);

	const matchupResult = game.series_matchups_result?.toUpperCase();

	let posessionTeam = 'hide';
	const teamPossessionId = game.team_possession_uuid || game.team_possession_id;
	if (teamPossessionId === game.visiting_team?.id) posessionTeam = 'visiting';
	if (teamPossessionId === game.home_team?.id) posessionTeam = 'home';

	const commonProps = {
		visitingTeamName,
		visitingTeamLogo,
		homeTeamName,
		homeTeamLogo,
		link: preserveQueryParams(`${game.new_game_url}`),
	};

	if (gameStatus === 'Pre-Game') {
		return {
			...commonProps,
			visitingTeamData: game.new_visiting_odds || '\u00A0',
			homeTeamData: game.new_home_odds || '\u00A0',
			teamDataStyle: 'odds',
			footerText: matchupResult,
			renderGameStatus: () => {
				return (
					<>
						{options.showDate ? (
							<div className='game_date'>
								{gameDate}, {gameTime}
							</div>
						) : (
							<div className='game_date'>{gameTime}</div>
						)}
						{broadcastName && <div className='broadcastName'>{broadcastName}</div>}
					</>
				);
			},
		};
	}
	if (gameStatus === 'In-Progress') {
		const gameStatus = game.clock === 'HT' ? 'HALF' : ordinalSuffix(game.period);
		return {
			...commonProps,
			visitingTeamData: `${game.visiting_team?.score}`,
			homeTeamData: `${game.home_team?.score}`,
			footerText: broadcastString,
			posession: posessionTeam,
			renderGameStatus: () => {
				return <div>{gameStatus}</div>;
			},
		};
	}
	if (gameStatus === 'Final') {
		return {
			...commonProps,
			visitingTeamData: `${game.visiting_team?.score}`,
			homeTeamData: `${game.home_team?.score}`,
			loser: gameLoser,
			footerText: matchupResult,
			renderGameStatus: () => <div>{game.new_status_long}</div>,
		};
	}

	// For games that are cancelled, postponed, forfeit, etc...
	return {
		...commonProps,
		visitingTeamData: '\u00A0',
		homeTeamData: '\u00A0',
		footerText: matchupResult,
		renderGameStatus: () => {
			return (
				<>
					<div>{gameStatus}</div>
				</>
			);
		},
	};
}

export function formatGameStats(homeTeam, visitingTeam) {
	if (!visitingTeam.game_stats || !homeTeam.game_stats) {
		return [];
	}

	const statCategories = [
		{
			title: 'Total Yards',
			valueKey: 'total_yards',
		},
		{
			title: 'Passing Yards',
			valueKey: 'passing_yards',
		},
		{
			title: 'Rushing Yards',
			valueKey: 'rushing_yards',
		},
		{
			title: 'Total Plays',
			valueKey: 'game_total_plays',
		},
		{
			title: 'Avg. Yards per Play',
			valueKey: 'game_total_average',
			valueFormatter: n => formatNumberWithDecimal(n, 1),
		},
		{
			title: 'First Downs',
			valueKey: 'first_downs',
		},
		(visitingTeam.game_stats.penalties || homeTeam.game_stats.penalties) && {
			title: 'Penalties',
			valueKey: 'penalties',
		},
		(visitingTeam.game_stats.penalty_yards || homeTeam.game_stats.penalty_yards) && {
			title: 'Penalty Yards',
			valueKey: 'penalty_yards',
		},
		{ title: 'Turnovers', valueKey: 'turnovers' },
		{ title: 'Tackles', valueKey: 'tackles' },
		{
			title: 'QB Sacks',
			valueKey: 'sacks',
		},
		{
			title: 'Sack Yards Lost',
			valueKey: 'yards_lost',
		},
		{ title: 'Time of Possession', valueKey: 'possession_time' },
	];

	const gameStats = statCategories.map(statCategory => {
		let homeTeamValue = homeTeam.game_stats[statCategory.valueKey];
		let visitingTeamValue = visitingTeam.game_stats[statCategory.valueKey];

		if (typeof homeTeamValue === 'string' || homeTeamValue instanceof String) {
			homeTeamValue = parseFloat(homeTeamValue) || 0;
		}
		if (typeof visitingTeamValue === 'string' || visitingTeamValue instanceof String) {
			visitingTeamValue = parseFloat(visitingTeamValue) || 0;
		}

		if (statCategory.valueKey === 'possession_time') {
			homeTeamValue = `${homeTeam.game_stats.possession_minutes}:${homeTeam.game_stats.possession_seconds}`;
			visitingTeamValue = `${visitingTeam.game_stats.possession_minutes}:${visitingTeam.game_stats.possession_seconds}`;
		}

		if ((!homeTeamValue && homeTeamValue !== 0) || (!visitingTeamValue && visitingTeamValue !== 0)) {
			return null;
		}

		return {
			title: statCategory.title,
			homeTeamValue: homeTeamValue,
			visitingTeamValue: visitingTeamValue,
			valueFormatter: statCategory.valueFormatter,
		};
	});
	return gameStats.filter(n => n); // filter out null values
}
